import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, FileDown, Loader2, Plus, Trash2 } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import { TopBar } from "@/components/fly/Logo";
import { FileUpload } from "@/components/fly/FileUpload";
import { MEDIA_OPTIONS, MediaRequirements } from "@/components/fly/SignupMedia";
import { DeadlineBanner, StartCountdown } from "@/components/fly/SignupDeadline";
import { useT } from "@/lib/i18n";

const CONSENT_URL = "https://strefafly.pl/wp-content/uploads/2026/01/FLYCUP2026-zgoda.pdf";
const input = "w-full h-11 px-3 rounded-xl bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none text-sm";
const emptyMember = () => ({ name: "", birth_year: "", consents: [] });
const Section = ({ n, title, children }) => (
  <section className="fly-card p-5 sm:p-6">
    <h2 className="font-display text-xl font-bold uppercase"><span className="text-blue-400 mr-2">{n}.</span>{title}</h2>
    <div className="mt-4 grid gap-3">{children}</div>
  </section>
);
const Field = ({ label, children }) => <label className="text-xs text-slate-400 grid gap-1">{label}{children}</label>;
const Box = ({ label, children }) => <div className="text-xs text-slate-400 grid gap-1">{label}{children}</div>;
const NEEDS = { music: ["music"], music_photo: ["music", "photo"], video: ["video"] };

const ConsentList = ({ items, onChange, testid }) => {
  const { t } = useT();
  return (
  <div className="grid gap-2" data-testid={testid}>
    {items.map((c, j) => (
      <FileUpload key={c.id} kind="consent" accept=".pdf,.jpg,.jpeg,.png" label={t("su.attach")} value={c} testid={`${testid}-file-${j}`}
        onChange={(v) => onChange(v ? items.map((x, k) => (k === j ? v : x)) : items.filter((_, k) => k !== j))} />
    ))}
    {items.length < 10 && <FileUpload kind="consent" accept=".pdf,.jpg,.jpeg,.png" label={items.length ? t("su.attachMore") : t("su.attach")}
      value={null} onChange={(v) => v && onChange([...items, v])} testid={`${testid}-add`} />}
  </div>
  );
};

export default function Signup() {
  const { t, lang } = useT();
  const [opt, setOpt] = useState(null);
  const [closed, setClosed] = useState(false);
  const [start, setStart] = useState(null);
  const [f, setF] = useState({ contact_name: "", email: "", phone: "", school: "", instructor: "", category: "", age_group: "", team_name: "", notes: "", media: "music", accept_rules: false });
  const [members, setMembers] = useState([emptyMember()]);
  const [files, setFiles] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const loadOptions = () => {
    setClosed(false); setStart(null);
    axios.get(`${API}/public/signup/options`).then((r) => setOpt(r.data)).catch((e) => {
      setClosed((lang === "pl" && e?.response?.data?.detail) || t("su.closed"));
      axios.get(`${API}/public/home`).then((h) => setStart(h.data.signup_start)).catch(() => {});
    });
  };
  useEffect(() => { loadOptions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cat = useMemo(() => opt?.categories.find((c) => c.category === f.category), [opt, f.category]);
  const fee = cat ? (cat.fee_per_member ? cat.fee * members.length : cat.fee) : null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setMember = (i, patch) => setMembers((ms) => ms.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  const setFile = (k) => (v) => setFiles((fs) => ({ ...fs, [k]: v }));

  const pickCat = (e) => {
    const c = opt.categories.find((x) => x.category === e.target.value);
    setF({ ...f, category: e.target.value, age_group: "" });
    if (c && !c.is_team) setMembers([members[0]]);
    if (c?.is_team && members.length < 2) setMembers([...members, emptyMember()]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (members.some((m) => !m.consents.length)) { toast.error(t("su.needConsent")); return; }
    setBusy(true);
    try {
      const body = { ...f, members: members.map((m) => ({ name: m.name.trim(), birth_year: Number(m.birth_year), consent_ids: m.consents.map((c) => c.id) })),
        files: Object.fromEntries(Object.entries(files).filter(([k]) => NEEDS[f.media].includes(k)).map(([k, v]) => [k, v?.id])) };
      const { data } = await axios.post(`${API}/public/signups`, body);
      setDone(data);
      window.scrollTo(0, 0);
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  if (closed && start) return <Shell><StartCountdown iso={start} onStart={() => setTimeout(loadOptions, 1500)} /></Shell>;
  if (closed) return <Shell><div data-testid="signup-closed" className="fly-card p-10 mt-8 text-center text-slate-400">{closed}</div></Shell>;
  if (!opt) return <Shell><p className="text-slate-400 mt-8">{t("su.loading")}</p></Shell>;
  if (done) return <Shell><Success d={done} name={f.team_name || members.map((m) => m.name).join(", ")} /></Shell>;

  return (
    <Shell>
      <DeadlineBanner iso={opt.deadline} start={opt.start} />
      {opt.deadline && <StartCountdown iso={opt.deadline} label="cd.endsIn" caption="cd.endsCaption" tid="signup-end-countdown" onStart={() => setTimeout(loadOptions, 1500)} />}
      <p className="text-sm text-slate-400 mt-2 max-w-3xl">{t("su.intro")}</p>
      <form onSubmit={submit} className="grid gap-5 mt-6" data-testid="signup-form">
        <Section n={1} title={t("su.s1")}>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label={t("su.name")}><input data-testid="signup-contact-name" required value={f.contact_name} onChange={set("contact_name")} className={input} /></Field>
            <Field label={t("su.email")}><input data-testid="signup-email" type="email" required value={f.email} onChange={set("email")} className={input} /></Field>
            <Field label={t("su.phone")}><input data-testid="signup-phone" required value={f.phone} onChange={set("phone")} className={input} /></Field>
            <Field label={t("su.school")}><input data-testid="signup-school" value={f.school} onChange={set("school")} className={input} placeholder={t("su.schoolPh")} /></Field>
            <Field label={t("su.instructor")}><input data-testid="signup-instructor" value={f.instructor} onChange={set("instructor")} className={input} /></Field>
          </div>
        </Section>

        <Section n={2} title={t("su.s2")}>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label={t("su.category")}>
              <select data-testid="signup-category" required value={f.category} onChange={pickCat} className={input}>
                <option value="">{t("su.pick")}</option>
                {opt.categories.map((c) => <option key={c.category} value={c.category} disabled={c.full}>{c.category}{c.full ? ` – ${t("su.full")}` : ""}</option>)}
              </select>
            </Field>
            <Field label={t("su.age")}>
              <select data-testid="signup-age" required={!!cat?.age_groups.length} value={f.age_group} onChange={set("age_group")} className={input}>
                <option value="">{t("su.pick")}</option>
                {(cat?.age_groups || []).map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
            {cat?.is_team && <Field label={t("su.team")}><input data-testid="signup-team-name" value={f.team_name} onChange={set("team_name")} className={input} /></Field>}
          </div>
          {fee !== null && <p data-testid="signup-fee" className="text-sm">{t("su.fee")} <b className="text-amber-300">{fee} {t("su.currency")}</b>{cat.fee_per_member && ` (${cat.fee} ${t("su.perMember")})`}</p>}
        </Section>

        <Section n={3} title={t("su.s3")}>
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-100">
            {t("su.consentInfo1")} <b>{t("su.consentInfoB")}</b> {t("su.consentInfo2")}
            <a data-testid="signup-consent-download" href={CONSENT_URL} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-amber-500 text-black font-semibold hover:bg-amber-400"><FileDown className="w-4 h-4" />{t("su.consentDl")}</a>
          </div>
          {members.map((m, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_130px_1fr_auto] gap-3 items-start rounded-xl bg-[#0B132B]/60 border border-white/5 p-3" data-testid={`signup-member-${i}`}>
              <Field label={t("su.member", { n: i + 1 })}><input data-testid={`signup-member-name-${i}`} required value={m.name} onChange={(e) => setMember(i, { name: e.target.value })} className={input} /></Field>
              <Field label={t("su.year")}><input data-testid={`signup-member-year-${i}`} required type="number" min="1930" max="2025" value={m.birth_year} onChange={(e) => setMember(i, { birth_year: e.target.value })} className={input} /></Field>
              <Box label={t("su.consents")}><ConsentList items={m.consents} onChange={(v) => setMember(i, { consents: v })} testid={`signup-consent-${i}`} /></Box>
              {cat?.is_team && members.length > 2 && <button type="button" data-testid={`signup-member-remove-${i}`} onClick={() => setMembers(members.filter((_, j) => j !== i))} className="h-11 w-11 rounded-xl text-red-300 hover:bg-red-500/10 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>}
            </div>
          ))}
          {cat?.is_team && <button type="button" data-testid="signup-member-add" onClick={() => setMembers([...members, emptyMember()])} className="h-10 px-4 rounded-xl border border-blue-500/30 text-blue-200 text-sm flex items-center gap-2 justify-self-start"><Plus className="w-4 h-4" />{t("su.addMember")}</button>}
        </Section>

        <Section n={4} title={t("su.s4")}>
          <div className="grid sm:grid-cols-3 gap-3">
            {MEDIA_OPTIONS.map((o) => (
              <button type="button" key={o.key} data-testid={`signup-media-${o.key}`} onClick={() => setF({ ...f, media: o.key })}
                className={`rounded-xl border p-4 text-left flex gap-3 ${f.media === o.key ? "border-blue-500 bg-blue-600/15" : "border-white/10 hover:border-blue-500/40"}`}>
                <o.icon className="w-5 h-5 text-blue-400 shrink-0" /><span><b className="block">{t(`media.${o.key}`)}</b><span className="text-xs text-slate-400">{t(`media.${o.key}.desc`)}</span></span>
              </button>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {f.media !== "video" && <Box label={t("su.music")}><FileUpload kind="music" accept=".mp3,.wav,.flac,audio/*" label={t("su.musicBtn")} value={files.music} onChange={setFile("music")} testid="signup-music" /></Box>}
            {f.media === "music_photo" && <Box label={t("su.photo")}><FileUpload kind="photo" accept=".jpg,.jpeg,image/jpeg" label={t("su.photoBtn")} value={files.photo} onChange={setFile("photo")} testid="signup-photo" /></Box>}
            {f.media === "video" && <Box label={t("su.video")}><FileUpload kind="video" accept=".mp4,video/mp4" label={t("su.videoBtn")} value={files.video} onChange={setFile("video")} testid="signup-video" /></Box>}
          </div>
          <MediaRequirements />
        </Section>

        <Section n={5} title={t("su.s5")}>
          <Field label={t("su.notes")}><textarea data-testid="signup-notes" rows={3} value={f.notes} onChange={set("notes")} className={`${input} h-auto py-2`} /></Field>
          <label className="flex items-start gap-3 text-sm">
            <input data-testid="signup-accept" type="checkbox" required checked={f.accept_rules} onChange={(e) => setF({ ...f, accept_rules: e.target.checked })} className="mt-1 h-4 w-4" />
            <span>{t("su.accept1")} <a href="https://strefafly.pl/regulamin/" target="_blank" rel="noreferrer" className="text-blue-300 underline">{t("su.rules")}</a>{t("su.accept2")}</span>
          </label>
          <button data-testid="signup-submit" disabled={busy} className="h-12 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("su.submit")}
          </button>
        </Section>
      </form>
    </Shell>
  );
}

const Shell = ({ children }) => {
  const { t } = useT();
  const [ev, setEv] = useState(null);
  useEffect(() => { axios.get(`${API}/public/home`).then((r) => setEv(r.data)).catch(() => {}); }, []);
  return (
  <>
    <TopBar lang title={t("su.title")} />
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <p data-testid="signup-event-info" className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400">FLY CUP 2026{ev && ` · ${ev.event_place} · ${ev.event_dates}`}</p>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase">{t("su.title")}</h1>
      {children}
    </main>
  </>
  );
};

const Success = ({ d, name }) => {
  const { t } = useT();
  return (
  <div data-testid="signup-success" className="fly-card p-6 sm:p-8 mt-8 border-emerald-500/40">
    <CheckCircle2 className="w-10 h-10 text-emerald-400" />
    <h2 className="font-display text-3xl font-bold uppercase mt-3">{t("su.sent")}</h2>
    <p className="mt-2">{t("su.number")} <b data-testid="signup-number" className="text-blue-300 text-xl">{d.number}</b></p>
    <p className="text-sm text-slate-400 mt-1">{t("su.keep")}</p>
    <div className="mt-5 rounded-xl bg-[#0B132B] border border-white/5 p-4 text-sm grid gap-1" data-testid="signup-payment">
      <div>{t("su.amount")} <b className="text-amber-300">{d.fee} {t("su.currency")}</b></div>
      <div>{t("su.payee")} {d.bank.name}, {d.bank.bank}</div>
      <div>{t("su.account")} <b className="font-mono">{d.bank.account}</b> · SWIFT/BIC: {d.bank.swift}</div>
      <div>{t("su.ref")} <b>FLY CUP 2026 – {d.number} – {name}</b></div>
      <div className="text-amber-200 mt-2">{t("su.payIn3")}</div>
    </div>
  </div>
  );
};
